import React, { useMemo } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';

// Same purple→indigo gradient as ExploreLightbox's primary CTA. Reused here
// so every "do the magic" button across the product reads as one family.
const GRADIENT_COLORS = ['#7c3aed', '#4338ca'];

/**
 * The big, delightful "Run this tool" CTA. A vibrant purple → indigo
 * gradient pill, full-width by default. Stays visually static (no hover
 * lift, no transitions) because the form already provides enough motion
 * via field-level affordances.
 */
function ToolRunButton({
  label,
  loadingLabel,
  icon = 'wand-magic-sparkles',
  isLoading = false,
  isDisabled = false,
  onPress,
  isFullWidth = true,
}) {
  const styles = useMemo(() => StyleSheet.create({
    wrapper: {
      borderRadius: 14,
      overflow: 'hidden',
      alignSelf: isFullWidth ? 'stretch' : 'flex-start',
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    },
    wrapperDisabled: {
      opacity: 0.55,
      ...(Platform.OS === 'web' && { cursor: 'not-allowed' }),
    },
    gradient: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 9,
      paddingVertical: 14,
      paddingHorizontal: 22,
      borderRadius: 14,
    },
    iconWrap: {
      width: 18,
      height: 18,
      alignItems: 'center',
      justifyContent: 'center',
    },
    label: {
      color: '#ffffff',
      letterSpacing: 0.2,
    },
  }), [isFullWidth]);

  const showLoading = !!isLoading;
  const text        = showLoading ? (loadingLabel || label) : label;

  return (
    <Pressable
      onPress={onPress}
      disabled={isDisabled || showLoading}
      style={[
        styles.wrapper,
        (isDisabled || showLoading) && styles.wrapperDisabled,
      ]}
    >
      <LinearGradient
        colors={GRADIENT_COLORS}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.gradient}
      >
        <View style={styles.iconWrap}>
          {showLoading
            ? <ActivityIndicator size='small' color='#ffffff' />
            : <Icon name={icon} size={14} color='#ffffff' solid />}
        </View>
        <Text size='s' weight='bold' style={styles.label}>
          {text}
        </Text>
      </LinearGradient>
    </Pressable>
  );
}

export default React.memo(ToolRunButton);
